/**
 * "Safety sign-off" (help `cloud.safety-signoff`; MB's R119, R120; docs/specs/jsp-safety-meeting.md §6; the JSA, R13,
 * docs/specs/jsa.md §5): each JSP safety meeting and JSA the crews sent for signing through the organisation's
 * DocuSeal account, newest first, with each
 * signer's state, the unsigned and signed PDFs and the audit log (5-minute links), "Check DocuSeal now" and
 * "Withdraw". Every staff role with access to the project; clients never (the database gives them no request).
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  SIGNOFF_DOCUMENT_WORDS,
  signerStatusWords,
  signoffDate,
  signoffStateWords,
  signoffTone,
  type SignoffDocumentKind,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { loadSignoffs, signoffAction, signoffFileUrl, type SignoffListRow } from './api.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const CHIP = { pass: 'chip chip--pass', warn: 'chip chip--warn', fail: 'chip chip--fail' } as const;

export interface SafetySignoffScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantName: string;
  readonly roles: readonly string[];
  /** Opens a 5-minute link (a new tab); tests pass their own. */
  readonly openUrl?: (url: string) => void;
}

export function SafetySignoffScreen({
  sb,
  tenantId,
  tenantName,
  roles,
  openUrl = (u) => void window.open(u, '_blank', 'noopener,noreferrer'),
}: SafetySignoffScreenProps): ReactElement {
  const [rows, setRows] = useState<SignoffListRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const withdrawer = roles.includes('tenant_admin') || roles.includes('manager');

  const reload = useCallback(() => {
    setError(null);
    loadSignoffs(sb, tenantId)
      .then(setRows)
      .catch((e: unknown) => setError(message(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  async function file(r: SignoffListRow, which: 'unsigned' | 'signed' | 'audit'): Promise<void> {
    setNotice(null);
    try {
      openUrl(await signoffFileUrl(sb, r.id, which));
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    }
  }

  async function act(r: SignoffListRow, action: 'refresh' | 'withdraw'): Promise<void> {
    setBusy(`${action}:${r.id}`);
    setNotice(null);
    try {
      await signoffAction(sb, action, r.id);
      setNotice({
        ok: true,
        text:
          action === 'refresh'
            ? `DocuSeal was asked about ${r.title}.`
            : `${r.title} was withdrawn: no one else can sign it. The crew can send it again from Teslyn Field.`,
      });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  }

  return (
    <section className="page page--wide">
      <PageHeader
        title="Safety sign-off"
        subtitle={`The JSP safety meetings and JSAs ${tenantName} crews sent for signing from Teslyn Field, through your organisation's DocuSeal account.`}
      />
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!rows && !error && <Loading what="sign-offs" />}
      {rows && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="label">Project</th>
                <th className="label">Document</th>
                <th className="label">Sent</th>
                <th className="label">State</th>
                <th className="label">Signers</th>
                <th className="label">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    Nothing sent for sign-off yet. In Teslyn Field, JSP safety meeting or JSA, make the PDF
                    and press &quot;Send for sign-off&quot;.
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const canWithdraw = withdrawer && r.state !== 'completed' && r.state !== 'withdrawn';
                return (
                  <tr key={r.id}>
                    <td className="body">
                      <span className="data">{r.project_number}</span> {r.project_title}
                    </td>
                    <td className="body">
                      {SIGNOFF_DOCUMENT_WORDS[r.document_kind as SignoffDocumentKind] ?? r.document_kind}
                      <div className="caption muted">
                        {r.title}, revision {r.document_revision}
                      </div>
                    </td>
                    <td className="caption">
                      {r.sent_at ? signoffDate(r.sent_at) : '–'}
                      {r.created_by_name && <div className="muted">by {r.created_by_name}</div>}
                    </td>
                    <td className="caption">
                      <span className={CHIP[signoffTone(r.state)]}>{signoffStateWords(r.state)}</span>
                      {r.completed_at && <div className="muted">{signoffDate(r.completed_at)}</div>}
                      {r.message && <div className="muted">{r.message}</div>}
                    </td>
                    <td className="caption">
                      <ul className="stack" aria-label={`Signers of ${r.title}`}>
                        {r.signers.map((s) => (
                          <li key={s.person_id}>
                            {s.name}: {signerStatusWords(s)}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="table__actions">
                      {confirming === r.id ? (
                        <>
                          <span className="caption">No one else can sign it.</span>
                          <Button
                            variant="primary"
                            busy={busy === `withdraw:${r.id}`}
                            onClick={() => void act(r, 'withdraw')}
                          >
                            Withdraw
                          </Button>
                          <Button variant="quiet" onClick={() => setConfirming(null)}>
                            Keep
                          </Button>
                        </>
                      ) : (
                        <>
                          {r.state !== 'failed' && (
                            <Button
                              variant="quiet"
                              aria-label={`Unsigned PDF of ${r.title}`}
                              onClick={() => void file(r, 'unsigned')}
                            >
                              Unsigned PDF
                            </Button>
                          )}
                          {r.signed_available && (
                            <>
                              <Button
                                variant="quiet"
                                aria-label={`Signed PDF of ${r.title}`}
                                onClick={() => void file(r, 'signed')}
                              >
                                Signed PDF
                              </Button>
                              <Button
                                variant="quiet"
                                aria-label={`Audit log of ${r.title}`}
                                onClick={() => void file(r, 'audit')}
                              >
                                Audit log
                              </Button>
                            </>
                          )}
                          {(r.state === 'sent' || r.state === 'sending') && (
                            <Button
                              variant="quiet"
                              busy={busy === `refresh:${r.id}`}
                              aria-label={`Check DocuSeal now about ${r.title}`}
                              onClick={() => void act(r, 'refresh')}
                            >
                              Check DocuSeal now
                            </Button>
                          )}
                          {canWithdraw && (
                            <Button
                              variant="quiet"
                              aria-label={`Withdraw ${r.title}`}
                              onClick={() => setConfirming(r.id)}
                            >
                              Withdraw
                            </Button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
